import {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '@features/auth/context/AuthContext';
import { useIdentityBroker } from '@core/commerce/identity/identityBroker';
import { trackEvent } from '@core/events';
import { resolveLineShopId } from '@core/utils/cartLineUtils';
import {
  getCartItemCount as countCartItems,
  getCartTotal as sumCartTotal,
  replaceOrAppendCartItem,
  updateCartLineQuantity,
} from '@core/commerce/cart/cartEngine';
import {
  mergeAnonymousCartIntoOwner,
  readCartItems,
  subscribeCartStorage,
  writeCartItems,
} from '@core/commerce/cart/cartStorage';
import { CartItem } from '../types';
import '@features/wishlist/context/WishlistToast.css';

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: Omit<CartItem, 'id'>) => Promise<void>;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getCartItemCount: () => number;
  getCartTotal: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

function CartProviderInner({ children }: CartProviderProps) {
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const { user } = useAuth();
  const { resolveShopCustomer } = useIdentityBroker();

  const cartOwnerId = user?.uid ?? null;
  const [cartItems, setCartItems] = useState<CartItem[]>(() =>
    readCartItems(cartOwnerId)
  );

  const showToast = useCallback((message: string) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  }, []);

  useEffect(() => {
    if (user?.uid) {
      setCartItems(mergeAnonymousCartIntoOwner(user.uid));
      return;
    }
    setCartItems(readCartItems(null));
  }, [user?.uid]);

  useEffect(() => {
    return subscribeCartStorage(cartOwnerId, nextItems => {
      setCartItems(nextItems);
    });
  }, [cartOwnerId]);

  const setCartItemsAndPersist = useCallback(
    (updater: CartItem[] | ((items: CartItem[]) => CartItem[])) => {
      setCartItems(currentItems => {
        const nextItems =
          updater instanceof Function ? updater(currentItems) : updater;
        return writeCartItems(cartOwnerId, nextItems);
      });
    },
    [cartOwnerId]
  );

  const addToCart = useCallback(async (item: Omit<CartItem, 'id'>) => {
    if (user?.email) {
      try {
        const sid = resolveLineShopId(item as CartItem);
        if (sid) {
          const customer = await resolveShopCustomer(sid);
          if (!customer?.id) {
            showToast('Could not prepare this store profile. Please try again.');
            return;
          }
        }
      } catch (e) {
        console.error('[cart] resolveShopCustomer failed:', e);
        showToast('Could not prepare this store profile. Please try again.');
        return;
      }
    }

    setCartItemsAndPersist(currentItems =>
      replaceOrAppendCartItem(currentItems, item)
    );
    const shopId = resolveLineShopId(item as CartItem);
    if (shopId) {
      trackEvent('add_to_cart', {
        shop_id: shopId,
        product_id: item.productId,
        surface: 'cart',
        quantity: item.quantity,
        price: item.price,
        currency: 'INR',
      });
    }
    showToast('Product Added to cart');
  }, [resolveShopCustomer, setCartItemsAndPersist, showToast, user?.email]);

  const removeFromCart = useCallback((id: string) => {
    const item = cartItems.find(current => current.id === id);
    setCartItemsAndPersist(currentItems =>
      currentItems.filter((item) => item.id !== id)
    );
    if (item) {
      const shopId = resolveLineShopId(item);
      if (shopId) {
        trackEvent('remove_from_cart', {
          shop_id: shopId,
          product_id: item.productId,
          surface: 'cart',
          quantity: item.quantity,
          price: item.price,
          currency: 'INR',
        });
      }
    }
  }, [cartItems, setCartItemsAndPersist]);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    setCartItemsAndPersist(currentItems =>
      updateCartLineQuantity(currentItems, id, quantity)
    );
  }, [setCartItemsAndPersist]);

  const clearCart = useCallback(() => {
    setCartItemsAndPersist([]);
  }, [setCartItemsAndPersist]);

  const getCartItemCount = useCallback(
    () => countCartItems(cartItems),
    [cartItems]
  );

  const getCartTotal = useCallback(
    () => sumCartTotal(cartItems),
    [cartItems]
  );

  const value = useMemo(
    () => ({
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartItemCount,
      getCartTotal,
    }),
    [
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartItemCount,
      getCartTotal,
    ]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
      {toastMessage && (
        <div className="wishlist-toast">
          {toastMessage}
        </div>
      )}
    </CartContext.Provider>
  );
}

export const CartProvider = ({ children }: CartProviderProps) => (
  <CartProviderInner>{children}</CartProviderInner>
);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
